/**
 * Sync Service Hook
 *
 * Monitors online status and processes queued mutations.
 * Handles retry logic with exponential backoff.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabaseClient';
import {
  getQueuedMutations,
  getMutationsReadyForRetry,
  dequeueMutation,
  updateMutationRetry,
  enqueueMutation,
  hasPendingMutations,
  getQueueStats,
  QueuedMutation
} from '../utils/syncQueue';

interface SyncServiceOptions {
  userId?: string;
  childId?: number | null;
  enabled?: boolean;
}

interface SyncState {
  isOnline: boolean;
  isSyncing: boolean;
  pendingCount: number;
  failedCount: number;
  lastSyncAt: Date | null;
  lastError: string | null;
}

export const useSyncService = (options: SyncServiceOptions = {}) => {
  const { userId, childId, enabled = true } = options;

  const [syncState, setSyncState] = useState<SyncState>({
    isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
    isSyncing: false,
    pendingCount: 0,
    failedCount: 0,
    lastSyncAt: null,
    lastError: null
  });

  const processingRef = useRef(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Update queue stats
  const updateStats = useCallback(() => {
    if (!userId) return;
    const stats = getQueueStats(userId);
    setSyncState(prev => ({
      ...prev,
      pendingCount: stats.pending,
      failedCount: stats.failed
    }));
  }, [userId]);

  // Process a single mutation
  const processMutation = async (mutation: QueuedMutation): Promise<boolean> => {
    try {
      switch (mutation.type) {
        case 'home_objects': {
          // Use atomic RPC
          const objects = mutation.payload.map((obj: any) => ({
            element_id: obj.objectId,
            instance_id: obj.instanceId,
            x: obj.x,
            y: obj.y,
            scale: obj.scale,
            rotation: obj.rotation
          }));

          const { data, error } = await supabase.rpc('sync_home_objects', {
            p_user_id: mutation.userId,
            p_child_id: mutation.childId || null,
            p_objects: objects
          });

          if (error) throw error;
          if (data && !data.success) throw new Error(data.error);

          console.log(`✅ Home objects synced: ${data?.inserted || 0} objects`);
          return true;
        }

        case 'world_config': {
          // Use atomic RPC
          const { data, error } = await supabase.rpc('sync_world_config', {
            p_user_id: mutation.userId,
            p_child_id: mutation.childId || null,
            p_hero_config: mutation.payload.heroConfig,
            p_background_config: mutation.payload.backgroundConfig,
            p_selected_hero_id: mutation.payload.selectedHeroId
          });

          if (error) throw error;
          if (data && !data.success) throw new Error(data.error);

          console.log('✅ World config synced');
          return true;
        }

        case 'story_progress': {
          // Upsert completed stories
          const progressToUpsert = mutation.payload.map((storyId: number) => ({
            user_id: mutation.userId,
            child_id: mutation.childId || null,
            story_id: storyId,
            current_page: 'completed',
            completed: true,
            completed_at: new Date().toISOString(),
            last_read_at: new Date().toISOString(),
          }));

          const { error } = await supabase
            .from('story_progress')
            .upsert(progressToUpsert, {
              onConflict: 'user_id,child_id,story_id',
              ignoreDuplicates: false
            });

          if (error) throw error;

          console.log(`✅ Story progress synced: ${progressToUpsert.length} stories`);
          return true;
        }

        case 'item_instance': {
          // Insert new item instance
          const { error } = await supabase
            .from('user_item_instances')
            .insert(mutation.payload);

          if (error) throw error;

          console.log('✅ Item instance synced');
          return true;
        }

        default:
          console.warn(`⚠️ Unknown mutation type: ${mutation.type}`);
          return false;
      }
    } catch (error: any) {
      console.error(`❌ Error processing mutation ${mutation.id}:`, error);
      setSyncState(prev => ({
        ...prev,
        lastError: error.message || 'Unknown error'
      }));
      return false;
    }
  };

  // Process all ready mutations
  const processQueue = useCallback(async () => {
    if (!userId || !enabled || processingRef.current) return;

    const mutations = getMutationsReadyForRetry(userId);
    if (mutations.length === 0) return;

    processingRef.current = true;
    setSyncState(prev => ({ ...prev, isSyncing: true }));

    console.log(`🔄 Processing ${mutations.length} queued mutations...`);

    for (const mutation of mutations) {
      // Check if still online before each mutation
      if (!navigator.onLine) {
        console.log('📴 Went offline, pausing sync');
        break;
      }

      const success = await processMutation(mutation);

      if (success) {
        dequeueMutation(mutation.id);
      } else {
        updateMutationRetry(mutation.id);
      }
    }

    processingRef.current = false;
    setSyncState(prev => ({
      ...prev,
      isSyncing: false,
      lastSyncAt: new Date()
    }));
    updateStats();
  }, [userId, enabled, updateStats]);

  // Monitor online/offline status
  useEffect(() => {
    const handleOnline = () => {
      console.log('🌐 Back online, triggering sync...');
      setSyncState(prev => ({ ...prev, isOnline: true }));
      // Trigger sync after coming back online
      setTimeout(processQueue, 1000);
    };

    const handleOffline = () => {
      console.log('📴 Went offline');
      setSyncState(prev => ({ ...prev, isOnline: false }));
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [processQueue]);

  // Periodic sync check (every 30 seconds when online)
  useEffect(() => {
    if (!userId || !enabled) return;

    intervalRef.current = setInterval(() => {
      if (navigator.onLine && hasPendingMutations(userId)) {
        processQueue();
      }
    }, 30000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [userId, enabled, processQueue]);

  // Update stats on mount and when userId changes
  useEffect(() => {
    updateStats();
  }, [updateStats]);

  // Enqueue mutation with online check
  const enqueue = useCallback((
    type: QueuedMutation['type'],
    payload: any
  ) => {
    if (!userId) {
      console.log('⏭️ Skip enqueue (guest mode)');
      return null;
    }

    const mutation = enqueueMutation(type, payload, userId, childId);
    updateStats();

    // If online, try to sync immediately
    if (navigator.onLine) {
      setTimeout(processQueue, 100);
    }

    return mutation;
  }, [userId, childId, updateStats, processQueue]);

  // Force sync now
  const syncNow = useCallback(async () => {
    if (!navigator.onLine) {
      console.log('📴 Cannot sync: offline');
      return false;
    }
    await processQueue();
    return true;
  }, [processQueue]);

  return {
    ...syncState,
    enqueue,
    syncNow,
    hasPending: syncState.pendingCount > 0 || syncState.failedCount > 0
  };
};

export default useSyncService;
